import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TAB_VARIANTS } from '@/shared/components';
import type { Product } from '@/features/products/types/products.types';
import { CampaignCancellationsPanel } from './CampaignCancellationsPanel';
import { CampaignBannerPanel } from './CampaignBannerPanel';
import { CampaignCTHFPanel } from './CampaignCTHFPanel';
import { CampaignTransferRulePanel } from './CampaignTransferRulePanel';

type SubTab = 'cancellation' | 'banners' | 'cthf' | 'transfer';

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'cancellation', label: 'Cancellation' },
  { id: 'banners', label: 'Banner' },
  { id: 'cthf', label: 'CTHF' },
  { id: 'transfer', label: 'Transfer' },
];

interface Props {
  productId: string;
  product: Product;
}

export function ProductRulesPanel({ productId, product }: Props) {
  const [searchParams] = useSearchParams();
  const initialSubTab = (searchParams.get('subtab') as SubTab | null) ?? 'cancellation';
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(
    SUB_TABS.some((t) => t.id === initialSubTab) ? initialSubTab : 'cancellation',
  );

  return (
    <div>
      <Tabs
        tabs={SUB_TABS}
        activeTab={activeSubTab}
        onTabChange={setActiveSubTab}
        idPrefix="product-rules"
        ariaLabel="Product campaign rule sections"
        variant={TAB_VARIANTS.sub}
      >
        {activeSubTab === 'cancellation' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              Cancellation rules for this product&apos;s campaign
            </h2>
            <CampaignCancellationsPanel productId={productId} product={product} />
          </>
        )}
        {activeSubTab === 'banners' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              Agent banners for this product&apos;s campaign. If enabled the message will be displayed when administering plans sold under this campaign.
            </h2>
            <CampaignBannerPanel productId={productId} product={product} />
          </>
        )}
        {activeSubTab === 'cthf' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              Money-handling capabilities for this product&apos;s campaign
            </h2>
            <CampaignCTHFPanel productId={productId} product={product} />
          </>
        )}
        {activeSubTab === 'transfer' && (
          <>
            <h2 className="mb-4 text-base font-medium text-mp-foreground">
              Transfer-in / transfer-out rules for this product&apos;s campaign
            </h2>
            <CampaignTransferRulePanel productId={productId} product={product} />
          </>
        )}
      </Tabs>
    </div>
  );
}
